// API URL from environment variable only
const API_URL = process.env.REACT_APP_API_BASE_URL;

// Global flags to ensure Main API is called exactly once per session
let mainApiCalled = false;
let mainApiResponse = null;

// AuthService class
class AuthService {
    constructor() {
        // No MSAL instance needed for SAML
        this.isInitialized = false;
        // Add caching mechanism for auth status check
        this.authStatusPromise = null;
        this.lastAuthCheck = null;
        this.AUTH_CACHE_DURATION = 60000; // Increase to 60 seconds cache
        // Add flag to prevent multiple simultaneous calls
        this.isCheckingAuth = false;
        
        // Initialize Main API flags from session storage if available
        if (sessionStorage.getItem('mainApiCalled') === 'true') {
            mainApiCalled = true;
            try {
                const storedResponse = sessionStorage.getItem('mainApiResponse');
                if (storedResponse) {
                    mainApiResponse = JSON.parse(storedResponse);
                }
            } catch (error) {
                console.warn('Error parsing stored Main API response:', error);
            }
        }
    }

    async initialize() {
        // Simple initialization for SAML
        this.isInitialized = true;
        // Check if we have authentication data in session storage
        const isAuthenticated = sessionStorage.getItem('isAuthenticated');
        const user = sessionStorage.getItem('user');
        
        if (isAuthenticated === 'true' && user) {
            // Store auth in the cache immediately to avoid any API calls
            try {
                const userData = JSON.parse(user);
                this.lastAuthCheck = {
                    result: {
                        isAuthenticated: true,
                        user: userData
                    },
                    timestamp: Date.now() // Fresh timestamp to ensure cache is valid
                };
            } catch (error) {
                console.error('Error parsing user data during initialization:', error);
            }
        }
    }

    async login() {
        // SAML login will be handled by redirecting to the SAML login page
        // Preserve the current URL parameters for the authentication flow
        const currentParams = window.location.search;
        window.location.href = `/saml-login${currentParams}`;
    }

    async logout() {
        try {
            // Clear session storage
            sessionStorage.removeItem('isAuthenticated');
            sessionStorage.removeItem('user');
            sessionStorage.removeItem('authToken');
            sessionStorage.removeItem('mainApiCalled');
            sessionStorage.removeItem('mainApiResponse');
            
            // Reset Main API flags
            mainApiCalled = false;
            mainApiResponse = null;
            
            // Clear authentication status cache
            this.clearAuthStatusCache();
            
            // Fallback to local logout
            window.location.href = '/';
        } catch (error) {
            console.error('Error during logout:', error);
            throw error;
        }
    }

    async getAccessToken() {
        // For cookie-based authentication, we don't need to manually get the token
        // The browser will automatically include the HTTP-only cookie in requests
        return 'cookie-based-auth';
    }

    async acquireTokenForAPI() {
        // Same as getAccessToken for SAML implementation
        return this.getAccessToken();
    }

    async getCurrentUser() {
        try {
            const user = sessionStorage.getItem('user');
            if (!user) {
                throw new Error('No authenticated user found');
            }
            return JSON.parse(user);
        } catch (error) {
            console.error('Error getting current user:', error);
            throw error;
        }
    }

    // Utility function to get cookie value by name
    getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    }

    // Get auth token from cookies
    getAuthTokenFromCookie() {
        const token = this.getCookie('auth_token');
        return token;
    }

    isAuthenticated() {
        // Only check sessionStorage for initial client-side check
        // The real authentication check should be done via API call
        const isAuth = sessionStorage.getItem('isAuthenticated');
        return isAuth === 'true';
    }

    // Main method to check authentication status via API (cookie-based JWT)
    async checkAuthenticationStatus() {
        // Check for page refresh scenario
        const isPageRefresh = performance.getEntriesByType('navigation')[0]?.type === 'reload';
        
        // Get current URL path to determine context
        const currentPath = window.location.pathname;
        
        // Determine if we're in protected areas
        const isSamlLoginPage = currentPath === '/' || currentPath.includes('/login');
        const isProgressNotesPage = currentPath.includes('/ProgressNotes') || currentPath.includes('/progressnotes');
        const isAssessmentPage = currentPath.includes('/Assessment');
        const isProtectedPage = isProgressNotesPage || isAssessmentPage;
        
        // Always make API call on page refresh for protected pages
        if (isPageRefresh && isProtectedPage) {
            return this.makeAuthStatusApiCall();
        }
        
        // Track API calls with specific context flags for non-refresh scenarios
        const AUTH_CALL_BEFORE_SAML = 'authCallBeforeSaml';
        const AUTH_CALL_AFTER_REDIRECT = 'authCallAfterRedirect';
        
        // Check if the respective call has already been made
        const samlCallMade = sessionStorage.getItem(AUTH_CALL_BEFORE_SAML) === 'true';
        const redirectCallMade = sessionStorage.getItem(AUTH_CALL_AFTER_REDIRECT) === 'true';
        
        // Allow call before SAML login if we're on login page and that call hasn't been made yet
        if (isSamlLoginPage && !samlCallMade) {
            sessionStorage.setItem(AUTH_CALL_BEFORE_SAML, 'true');
            return this.makeAuthStatusApiCall();
        }
        
        // Allow call after redirect if we're on protected pages and that call hasn't been made yet
        if (isProtectedPage && !redirectCallMade) {
            sessionStorage.setItem(AUTH_CALL_AFTER_REDIRECT, 'true');
            return this.makeAuthStatusApiCall();
        }
        
        // For all other cases, use session data and don't call API
        // First check sessionStorage for persistent authentication state
        const isAuthInSession = sessionStorage.getItem('isAuthenticated') === 'true';
        const authUserData = sessionStorage.getItem('user');
        
        if (isAuthInSession && authUserData) {
            try {
                return {
                    isAuthenticated: true,
                    user: JSON.parse(authUserData)
                };
            } catch (error) {
                console.error('Error parsing user data from session storage:', error);
            }
        }
        
        // If not authenticated in session but we have a cached result, use it
        if (this.lastAuthCheck) {
            return this.lastAuthCheck.result;
        }
        
        // If no cache, return not authenticated without API call
        return { isAuthenticated: false, user: null };
    }

    // Helper method to make the actual API call and handle caching
    async makeAuthStatusApiCall() {
        // If we're already checking auth, return the existing promise
        if (this.isCheckingAuth && this.authStatusPromise) {
            return this.authStatusPromise;
        }
        
        // If we have a recent cached result, return it (no new API call)
        // Only use cache if we're not on the progress notes page to ensure fresh data after SAML flow
        const currentPath = window.location.pathname;
        const isProgressNotesPage = currentPath.includes('/progress-notes') || currentPath.includes('/progressnotes');
        
        if (!isProgressNotesPage && this.lastAuthCheck && 
            (Date.now() - this.lastAuthCheck.timestamp < this.AUTH_CACHE_DURATION)) {
            return this.lastAuthCheck.result;
        }

        // This is an allowed API call, proceed
        // Set flag to indicate we're checking auth
        this.isCheckingAuth = true;
        
        // Create a new auth status check promise
        this.authStatusPromise = this.performAuthStatusCheck();
        
        try {
            const result = await this.authStatusPromise;
            // Cache the result with timestamp
            this.lastAuthCheck = {
                result,
                timestamp: Date.now()
            };
            
            // If authenticated, save to session storage
            if (result.isAuthenticated) {
                sessionStorage.setItem('isAuthenticated', 'true');
                if (result.user) {
                    sessionStorage.setItem('user', JSON.stringify(result.user));
                }
            }
            
            return result;
        } catch (error) {
            console.error('Auth status check failed:', error);
            return { isAuthenticated: false };
        } finally {
            // Clear the checking flag and promise
            this.isCheckingAuth = false;
            this.authStatusPromise = null;
        }
    }

    // Separate method to perform the actual API call
    async performAuthStatusCheck() {
        try {
            const apiUrl = `${API_URL}/Login/check-auth-status`;
            
            console.log('🔍 Making check-auth API call to:', apiUrl);
            
            const response = await fetch(apiUrl, {
                method: 'GET',
                credentials: 'include', // Include cookies in the request
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache' // Prevent caching
                }
            });
            
            if (!response.ok) {
                console.log('❌ Check-auth API response not ok:', response.status);
                return { isAuthenticated: false };
            }

            const data = await response.json();
            console.log('✅ Check-auth API response:', data);
            
            const isAuthenticated = data?.IsAuthenticated || data?.isAuthenticated || false;
            
            if (isAuthenticated) {
                // Update sessionStorage with fresh data from API
                sessionStorage.setItem('isAuthenticated', 'true');
                const userInfo = {
                    userName: data?.Username || data?.username || '',
                    userId: data?.UserId || data?.userId || '',
                    email: data?.Email || data?.email || '',
                    roles: data?.Roles || data?.roles || [],
                    isAuthenticated: true
                };
                sessionStorage.setItem('user', JSON.stringify(userInfo));
            } else {
                // Clear sessionStorage if not authenticated
                sessionStorage.removeItem('isAuthenticated');
                sessionStorage.removeItem('user');
            }
            
            return { 
                isAuthenticated, 
                user: isAuthenticated ? JSON.parse(sessionStorage.getItem('user')) : null 
            };
        } catch (error) {
            console.error('❌ Error checking authentication status:', error);
            // Clear sessionStorage on error
            sessionStorage.removeItem('isAuthenticated');
            sessionStorage.removeItem('user');
            throw error; // Re-throw to be handled by caller
        }
    }

    // Method to clear cached auth status (useful for logout or when auth status changes)
    clearAuthStatusCache() {
        this.lastAuthCheck = null;
        this.authStatusPromise = null;
        this.isCheckingAuth = false;
        
        // Reset the API call flags on logout to allow new checks on next login cycle
        sessionStorage.removeItem('authCallBeforeSaml');
        sessionStorage.removeItem('authCallAfterRedirect');
    }

    // Method to force refresh auth status (bypasses cache but still respects the allowed API call contexts)
    async forceRefreshAuthStatus() {
        // Clear local cache
        this.lastAuthCheck = null;
        this.authStatusPromise = null;
        this.isCheckingAuth = false;
        
        // Get current URL path to determine context
        const currentPath = window.location.pathname;
        const isSamlLoginPage = currentPath === '/' || currentPath.includes('/login');
        const isProgressNotesPage = currentPath.includes('/progress-notes') || currentPath.includes('/progressnotes');
        
        // Check if the respective call has already been made
        const samlCallMade = sessionStorage.getItem('authCallBeforeSaml') === 'true';
        const redirectCallMade = sessionStorage.getItem('authCallAfterRedirect') === 'true';
        
        // Only allow API calls in the two specific contexts if they haven't been made yet
        if ((isSamlLoginPage && !samlCallMade) || (isProgressNotesPage && !redirectCallMade)) {
            return this.checkAuthenticationStatus();
        }
        
        // Check session storage first
        const isAuthInSession = sessionStorage.getItem('isAuthenticated') === 'true';
        const authUserData = sessionStorage.getItem('user');
        
        if (isAuthInSession && authUserData) {
            try {
                return {
                    isAuthenticated: true,
                    user: JSON.parse(authUserData)
                };
            } catch (error) {
                console.error('Error parsing user data:', error);
            }
        }       
        
        // If not in session, return unauthenticated
        return { isAuthenticated: false, user: null };
    }
}

// Create a single instance and export methods
const authServiceInstance = new AuthService();

export const getCurrentUser = () => authServiceInstance.getCurrentUser();
export const checkAuthenticationStatus = () => authServiceInstance.checkAuthenticationStatus();
export const clearAuthStatusCache = () => authServiceInstance.clearAuthStatusCache();
export const forceRefreshAuthStatus = () => authServiceInstance.forceRefreshAuthStatus();
export const authService = authServiceInstance;

// Function to get ID token (updated to work with check-auth flow)
export const getIdToken = async () => {
    try {
        // First check if we have an auth token in session storage
        const authToken = sessionStorage.getItem('authToken');
        if (authToken) {
            return authToken;
        }
        
        // If no token in session, try to get it from cookie
        const cookieToken = authServiceInstance.getAuthTokenFromCookie();
        if (cookieToken) {
            // Save to session for future use
            sessionStorage.setItem('authToken', cookieToken);
            return cookieToken;
        }
        
        // If no token available, check authentication status to potentially get a fresh token
        const authResult = await checkAuthenticationStatus();
        if (authResult.isAuthenticated) {
            // Try cookie again after auth check
            const newCookieToken = authServiceInstance.getAuthTokenFromCookie();
            if (newCookieToken) {
                sessionStorage.setItem('authToken', newCookieToken);
                return newCookieToken;
            }
        }
        
        // Return placeholder for cookie-based auth if no explicit token available
        return 'cookie-based-auth';
    } catch (error) {
        console.error('Error getting ID token:', error);
        return 'cookie-based-auth';
    }
};

export default authServiceInstance;


